import axios from 'axios';
import {
  GET_COMPANIES,
  GET_USER_ROLES,
  GET_USERS,
  VIEW_ENTITY_TYPE_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import {
  is_coordinator_role,
  map_user,
  normalize_entity_type_option,
  normalize_entity_type_option_name,
  parse_api_data,
  project_display_name,
} from './user_management_helpers';

export default {
  normalize_entity_type_option(item = {}, index = 0) {
    return normalize_entity_type_option(item, index);
  },

  normalize_entity_type_option_name(name) {
    return normalize_entity_type_option_name(name);
  },

  async fetch_entity_type_options() {
    if (this.entity_types_loading) return;

    this.entity_types_loading = true;

    try {
      const response = await axios.get(
        VIEW_ENTITY_TYPE_URL,
        get_request_config({
          params: {
            page: 1,
            page_size: 200,
          },
        })
      );
      const response_data = parse_api_data(response.data);
      const items = Array.isArray(response_data.data) ? response_data.data : [];
      const options = items
        .map((item, index) => this.normalize_entity_type_option(item, index))
        .filter((item) => item.id > 0 && item.is_active);

      const unique_options = [];
      const seen_ids = new Set();
      const seen_names = new Set();
      for (const option of options) {
        if (seen_ids.has(option.id)) continue;
        const normalized_name = this.normalize_entity_type_option_name(option.name);
        if (normalized_name && seen_names.has(normalized_name)) continue;
        seen_ids.add(option.id);
        if (normalized_name) {
          seen_names.add(normalized_name);
        }
        unique_options.push(option);
      }

      this.entity_type_options = unique_options;
    } catch {
      this.entity_type_options = [];
    } finally {
      this.entity_types_loading = false;
    }
  },

  async handle_create_role_change() {
    if (!is_coordinator_role(this.create_form.role)) {
      this.create_form.entity_type_id = '';
      return;
    }

    if (!this.entity_type_options.length) {
      await this.fetch_entity_type_options();
    }
  },

  get_selected_create_entity_type() {
    return this.entity_type_options.find(
      (item) => String(item.id) === String(this.create_form.entity_type_id)
    ) || null;
  },

  async handle_edit_role_change() {
    const selected_role = this.roles.find(
      (role) => String(role.id) === String(this.edit_form.role_id)
    );

    if (!is_coordinator_role(selected_role?.role)) {
      this.edit_form.entity_type_id = '';
      return;
    }

    if (!this.entity_type_options.length) {
      await this.fetch_entity_type_options();
    }
  },

  get_selected_edit_entity_type() {
    return this.entity_type_options.find(
      (item) => String(item.id) === String(this.edit_form.entity_type_id)
    ) || null;
  },

  get_selected_edit_entity_type_label() {
    return this.get_selected_edit_entity_type()?.name || this.edit_form.entity_type_name || '';
  },

  should_show_selected_edit_entity_type_fallback() {
    const selected_id = String(this.edit_form.entity_type_id || '').trim();
    if (!selected_id) return false;

    return !this.entity_type_options.some((item) => String(item.id) === selected_id);
  },

  async fetch_roles() {
    this.roles_loading = true;

    try {
      const response = await axios.get(GET_USER_ROLES, get_request_config());
      const response_data = parse_api_data(response.data);
      const roles = Array.isArray(response_data.data) ? response_data.data : [];
      this.roles = roles.filter((role) => role?.role);

      if (!this.create_form.role && this.roles.length) {
        this.create_form.role = this.roles[0].role;
      }
    } catch {
      this.roles = [];
    } finally {
      this.roles_loading = false;
    }
  },

  async fetch_projects() {
    this.projects_loading = true;

    try {
      const response = await axios.get(GET_COMPANIES, get_request_config());
      const response_data = parse_api_data(response.data);
      const items = response_data.data || response_data.projects || [];
      this.projects = Array.isArray(items) ? items : [];
    } catch {
      this.projects = [];
    } finally {
      this.projects_loading = false;
    }
  },

  async fetch_assigned_project_ids(user_id) {
    if (!user_id) return [];

    if (!this.projects.length) {
      await this.fetch_projects();
    }

    this.assigned_projects_loading = true;

    try {
      const responses = await Promise.allSettled(
        this.projects.map((project) =>
          axios.get(
            GET_USERS,
            get_request_config({
              params: {
                project_id: project.id,
                page: 1,
                page_size: 100,
              },
            })
          ).then((response) => ({ project, response }))
        )
      );

      return responses
        .filter((result) => result.status === 'fulfilled')
        .filter(({ value }) => {
          const response_data = parse_api_data(value.response.data);
          const users = Array.isArray(response_data.data) ? response_data.data : [];

          return users.some((user) => String(user.user_id || user.id) === String(user_id));
        })
        .map(({ value }) => String(value.project.id));
    } catch {
      return [];
    } finally {
      this.assigned_projects_loading = false;
    }
  },

  project_display_name(project) {
    return project_display_name(project);
  },

  async fetch_users() {
    this.is_loading = true;

    try {
      const response = await axios.get(
        GET_USERS,
        get_request_config({
          params: {
            page: 1,
            page_size: 100,
          },
        })
      );
      const response_data = parse_api_data(response.data);
      const items = Array.isArray(response_data.data) ? response_data.data : [];
      this.users = items.map(map_user).filter((user) => user.id != null);
      this.pagination = response_data.pagination || null;
    } catch {
      this.users = [];
      this.show_error('Failed to load users.');
    } finally {
      this.is_loading = false;
    }
  },
};
